<template>
    <div class="modal fade" id="cash_movement_modal" ref="modalEl" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered mw-520px">
            <div class="modal-content">
                <div class="modal-header">
                    <h2 class="fw-bolder">{{ $t('pages.cash_register.movement.addTitle') }}</h2>
                    <div data-bs-dismiss="modal" class="btn btn-icon btn-sm btn-active-icon-primary">
                        <span class="svg-icon svg-icon-1"><inline-svg src="/media/icons/duotune/arrows/arr061.svg"/></span>
                    </div>
                </div>

                <el-form @submit.prevent="onSubmit" :model="form" ref="formRef">
                    <div class="modal-body py-8 px-lg-10">
                        <div class="cmd-field">
                            <label class="cmd-label">{{ $t('pages.cash_register.movement.direction') }}</label>
                            <div class="cmd-segment">
                                <button type="button"
                                    class="cmd-segment-btn"
                                    :class="{'cmd-segment-btn--active cmd-segment-btn--in': form.direction_id === 1}"
                                    @click="form.direction_id = 1">
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
                                        <line x1="12" y1="19" x2="12" y2="5"/>
                                        <polyline points="5 12 12 5 19 12"/>
                                    </svg>
                                    {{ $t('pages.cash_register.movement.directionType.1') }}
                                </button>
                                <button type="button"
                                    class="cmd-segment-btn"
                                    :class="{'cmd-segment-btn--active cmd-segment-btn--out': form.direction_id === 2}"
                                    @click="form.direction_id = 2">
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
                                        <line x1="12" y1="5" x2="12" y2="19"/>
                                        <polyline points="19 12 12 19 5 12"/>
                                    </svg>
                                    {{ $t('pages.cash_register.movement.directionType.2') }}
                                </button>
                            </div>
                        </div>

                        <div class="cmd-field">
                            <label class="cmd-label cmd-label--required">{{ $t('pages.cash_register.movement.amount') }}</label>
                            <el-input v-model="form.amount"
                                oninput="value=value.replace(/[^0-9.]/g,'')"
                                :placeholder="$t('pages.cash_register.movement.amountPlaceholder')"/>
                        </div>

                        <div class="cmd-field">
                            <label class="cmd-label">{{ $t('pages.cash_register.movement.note') }}</label>
                            <el-input v-model="form.note" type="textarea" :rows="2"
                                :placeholder="$t('pages.cash_register.movement.notePlaceholder')"/>
                        </div>
                    </div>

                    <div class="modal-footer flex-center">
                        <button type="button" class="btn btn-light me-2" data-bs-dismiss="modal" :disabled="loading">
                            {{ $t('common.cancel') }}
                        </button>
                        <button type="submit" class="btn btn-primary"
                            :disabled="!canSubmit || loading"
                            :data-kt-indicator="loading ? 'on' : null">
                            <span v-if="!loading" class="indicator-label">{{ $t('common.save') }}</span>
                            <span v-if="loading" class="indicator-progress">
                                {{ $t('messages.wait') }}
                                <span class="spinner-border spinner-border-sm align-middle ms-2"></span>
                            </span>
                        </button>
                    </div>
                </el-form>
            </div>
        </div>
    </div>
</template>

<script>
function emptyForm() {
    return {
        direction_id: 1,
        amount: '',
        note: '',
    };
}

export default {
    name: "CashMovementDialog",
    props: {
        cashId: { type: [Number, String], required: true },
    },
    emits: ['created'],
    data() {
        return {
            form: emptyForm(),
            loading: false,
        };
    },
    computed: {
        canSubmit() {
            const n = parseFloat(this.form.amount);
            return n > 0;
        },
    },
    methods: {
        open() {
            this.form = emptyForm();
            this.showModal(this.$refs.modalEl);
        },
        async onSubmit() {
            if (!this.canSubmit) return;
            this.loading = true;
            const body = {
                direction_id: this.form.direction_id,
                amount: parseFloat(this.form.amount),
            };
            if (this.form.note) body.note = this.form.note;
            try {
                const res = await this.$store.dispatch('cash/movement/add', {
                    cashId: this.cashId,
                    formData: body,
                });
                if (res?.status) {
                    this.$message.success(this.$t('messages.saveOk'));
                    this.hideModal(this.$refs.modalEl);
                    this.$emit('created', res.data);
                }
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style scoped>
.cmd-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 14px;
}

.cmd-label {
    font-size: 12px;
    font-weight: 600;
    color: #1c1c1e;
}

.cmd-label--required::after {
    content: ' *';
    color: #ef4444;
}

html.dark .cmd-label,
html.midnight .cmd-label { color: #EAE6E1; }

.cmd-segment {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0;
    padding: 3px;
    background: rgba(0, 0, 0, 0.04);
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 9px;
}

.cmd-segment-btn {
    border: none;
    background: transparent;
    color: #8e8e93;
    font-size: 13px;
    font-weight: 600;
    font-family: inherit;
    padding: 8px 10px;
    border-radius: 6px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    transition: background 0.18s, color 0.18s, box-shadow 0.18s;
}

.cmd-segment-btn:hover { color: #1c1c1e; }

.cmd-segment-btn--active {
    background: #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.cmd-segment-btn--in.cmd-segment-btn--active { color: #28a745; }
.cmd-segment-btn--out.cmd-segment-btn--active { color: #ef4444; }

html.dark .cmd-segment,
html.midnight .cmd-segment {
    background: rgba(255, 255, 255, 0.04);
    border-color: rgba(255, 255, 255, 0.08);
}

html.dark .cmd-segment-btn--active,
html.midnight .cmd-segment-btn--active {
    background: rgba(255, 255, 255, 0.08);
}

html.dark .cmd-segment-btn--in.cmd-segment-btn--active,
html.midnight .cmd-segment-btn--in.cmd-segment-btn--active { color: #34D399; }

html.dark .cmd-segment-btn--out.cmd-segment-btn--active,
html.midnight .cmd-segment-btn--out.cmd-segment-btn--active { color: #F87171; }

html.dark .cmd-segment-btn:hover,
html.midnight .cmd-segment-btn:hover { color: #EAE6E1; }

/* Modal cancel button — Metronic btn-light tum temalarda override */
.btn.btn-light,
.btn.btn-light:hover,
.btn.btn-light:focus,
.btn.btn-light:active,
.btn.btn-light:focus-visible {
    border-color: rgba(0, 0, 0, 0.08) !important;
    color: #1c1c1e !important;
    box-shadow: none !important;
}

.btn.btn-light,
.btn.btn-light:focus { background-color: rgba(0, 0, 0, 0.05) !important; }
.btn.btn-light:hover,
.btn.btn-light:active { background-color: rgba(0, 0, 0, 0.09) !important; }

html.dark .btn.btn-light,
html.dark .btn.btn-light:hover,
html.dark .btn.btn-light:focus,
html.dark .btn.btn-light:active,
html.midnight .btn.btn-light,
html.midnight .btn.btn-light:hover,
html.midnight .btn.btn-light:focus,
html.midnight .btn.btn-light:active {
    border-color: rgba(255, 255, 255, 0.1) !important;
    color: #EAE6E1 !important;
}

html.dark .btn.btn-light,
html.dark .btn.btn-light:focus,
html.midnight .btn.btn-light,
html.midnight .btn.btn-light:focus { background-color: rgba(255, 255, 255, 0.07) !important; }
html.dark .btn.btn-light:hover,
html.dark .btn.btn-light:active,
html.midnight .btn.btn-light:hover,
html.midnight .btn.btn-light:active { background-color: rgba(255, 255, 255, 0.13) !important; }

html.cream .btn.btn-light,
html.cream .btn.btn-light:hover,
html.cream .btn.btn-light:focus,
html.cream .btn.btn-light:active {
    border-color: rgba(61, 56, 50, 0.1) !important;
    color: #3D3832 !important;
}

html.cream .btn.btn-light,
html.cream .btn.btn-light:focus { background-color: rgba(61, 56, 50, 0.06) !important; }
html.cream .btn.btn-light:hover,
html.cream .btn.btn-light:active { background-color: rgba(61, 56, 50, 0.1) !important; }

</style>
